import {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTodoMutations, useTodoSuggestions, useTodos} from '../api/todos';
import Button from '../components/Button';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import KindTile from '../components/tell/KindTile';
import TellRow from '../components/tell/TellRow';
import RemindSheet from '../components/todo/RemindSheet';
import TodoRow from '../components/todo/TodoRow';
import {useTellIt} from '../components/todo/useTellIt';
import {useMe} from '../hooks/useAuth';
import {toast} from '../hooks/useToast';
import {todoSections} from '../lib/todos';
import {nowLocal, offsetMinutes} from '../lib/when';
import {HistorySuggestion, Post} from '../types';

// The To do tab (wireframe "todo"): the gardener's own jobs by when (overdue, this week, later
// this season, next season, some time), then what the garden suggests (problems nobody has
// checked, jobs this gardener usually does about now), then the last fortnight's done. Ticking
// one off offers "Tell it". Suggestions are offered, never added until Add.

function HistoryCard({s, onAdd, onDismiss, busy}: {s: HistorySuggestion; onAdd: () => Promise<unknown>; onDismiss: () => void; busy: boolean}) {
    return (
        <div className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
                <KindTile kind={s.tellKind} size={40} />
                <div className="min-w-0 flex-1">
                    <div className="font-display text-lg font-[650] leading-[1.2]">{s.title}</div>
                    <p className="meta m-0 mt-1">{s.reason}</p>
                </div>
            </div>
            <div className="flex gap-2">
                <Button size="sm" busy={busy} busyLabel="Adding…" onClick={onAdd}>
                    <Icon name="plus" size={18} /> Add
                </Button>
                <Button size="sm" variant="ghost" onClick={onDismiss}>
                    Not this year
                </Button>
            </div>
        </div>
    );
}

export default function TodoPage() {
    const me = useMe();
    const todos = useTodos();
    const suggestions = useTodoSuggestions();
    const {create, done, reopen, dismiss} = useTodoMutations();
    const tellIt = useTellIt();
    const [showDone, setShowDone] = useState(false);
    const [reminding, setReminding] = useState<Post | null>(null);
    const [adding, setAdding] = useState<string | null>(null);

    const today = todos.data?.today ?? '';
    const sections = todos.data ? todoSections(todos.data.open, today, me.data?.hemisphere) : [];
    const problems = suggestions.data?.problems ?? [];
    const history = suggestions.data?.history ?? [];

    const tick = (todo: Post) => done.mutate(todo.id, {onSuccess: post => tellIt(todo, post.doneAt === null)});

    const add = (s: HistorySuggestion) => {
        setAdding(s.key);
        return create
            .mutateAsync({text: s.title, whenLocal: nowLocal(), whenOffsetMinutes: offsetMinutes(), dueLocal: s.dueLocal, plants: [s.plant], placeId: s.placeId, tellKind: s.tellKind})
            .then(() => {
                toast.success(`${s.title}: on your list.`);
                return suggestions.refetch();
            })
            .finally(() => setAdding(null));
    };

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="flex items-end justify-between pt-2">
                <div>
                    <h1 className="display m-0">To do</h1>
                    {todos.data && todos.data.open.length > 0 && <p className="meta mt-1">{todos.data.open.length} on the list</p>}
                </div>
                <Link to="/todo/new" className="btn btn-secondary btn-sm no-underline">
                    <Icon name="plus" size={18} /> New
                </Link>
            </header>

            {todos.isPending && <SkeletonList rows={4} />}
            {todos.isError && <ErrorBox error={todos.error} onRetry={() => void todos.refetch()} />}

            {todos.isSuccess && todos.data.open.length === 0 && (
                <EmptyState icon="reminder" title="Nothing on the list." action={<Link to="/todo/new" className="btn btn-primary no-underline">Add a job</Link>}>
                    Jobs for the garden, with a date or without. &ldquo;Remind me&rdquo; on a tell adds the next check for you.
                </EmptyState>
            )}

            {sections.map(section => (
                <section key={section.key} className="flex flex-col gap-2">
                    <h2 className={`band-label m-0 ${section.key === 'overdue' ? 'text-rust' : 'text-ink-2'}`}>{section.label}</h2>
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-3">
                        {section.todos.map(todo => (
                            <TodoRow key={todo.id} todo={todo} today={today} onDone={tick} />
                        ))}
                    </div>
                </section>
            ))}

            {problems.length > 0 && (
                <section className="flex flex-col gap-2">
                    <h2 className="band-label m-0 text-ink-2">Problems to check</h2>
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-4">
                        {problems.map(p => (
                            <div key={p.id} className="flex flex-col gap-1 py-2">
                                <TellRow post={p} to={`/tell/${p.id}`} />
                                <div className="flex gap-2 pb-1 pl-[52px]">
                                    <Button size="sm" variant="secondary" onClick={() => setReminding(p)}>
                                        <Icon name="reminder" size={16} /> Remind me
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => dismiss.mutate({key: `problem:${p.id}`, season: '-'})}>
                                        Sorted
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {history.length > 0 && (
                <section className="flex flex-col gap-2">
                    <h2 className="band-label m-0 text-ink-2">From other years</h2>
                    {history.map(s => (
                        <HistoryCard key={s.key} s={s} busy={adding === s.key} onAdd={() => add(s)} onDismiss={() => dismiss.mutate({key: s.key, season: s.season})} />
                    ))}
                </section>
            )}

            {todos.data && todos.data.done.length > 0 && (
                <section className="flex flex-col gap-2">
                    <button type="button" className="meta flex items-center gap-1 self-start font-semibold" aria-expanded={showDone} onClick={() => setShowDone(v => !v)}>
                        Done lately ({todos.data.done.length}) <Icon name="down" size={16} className={showDone ? 'rotate-180' : ''} />
                    </button>
                    {showDone && (
                        <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-3">
                            {todos.data.done.map(todo => (
                                <TodoRow key={todo.id} todo={todo} today={today} onReopen={t => reopen.mutate(t.id)} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {reminding && <RemindSheet key={reminding.id} post={reminding} onClose={() => { setReminding(null); void suggestions.refetch(); }} />}

            <Link
                to="/tell/new"
                aria-label="New tell"
                className="fixed bottom-[104px] right-4 z-10 flex h-16 w-16 items-center justify-center rounded-fab bg-shoot text-on-shoot shadow-float no-underline active:scale-95"
            >
                <Icon name="camera" size={30} />
            </Link>
        </div>
    );
}
